import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Home, TrendingUp, MessageCircle, User, Newspaper, Calendar, BarChart2, Briefcase, X, ExternalLink, Clock, Building } from 'lucide-react';

// Runtime detection - works in both localhost and production
const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api' 
  : '/api';

function NewsPage({ user, setUser }) {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNews, setSelectedNews] = useState(null);

  useEffect(() => {
    fetchNews();
    // Refresh news every 5 minutes
    const interval = setInterval(fetchNews, 300000);
    return () => clearInterval(interval);
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news`);
      // Yeni API direkt array döndürüyor
      const newsData = Array.isArray(response.data) ? response.data : (response.data.news || []);
      setNews(newsData);
    } catch (err) {
      console.error('News fetch error:', err);
      setNews([]);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'BORSA': 'bg-blue-900 text-blue-300',
      'EKONOMİ': 'bg-green-900 text-green-300',
      'DÖVİZ': 'bg-yellow-900 text-yellow-300',
      'TEKNOLOJİ': 'bg-purple-900 text-purple-300',
      'EMTİA': 'bg-orange-900 text-orange-300',
      'FİNANS': 'bg-emerald-900 text-emerald-300'
    };
    return colors[category] || 'bg-gray-900 text-gray-300';
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Tarih bilinmiyor';
    
    const now = new Date();
    const date = new Date(dateString);
    
    // Check if date is invalid
    if (isNaN(date.getTime())) {
      return 'Tarih bilinmiyor';
    }
    
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffMins < 1) return 'Az önce';
    if (diffMins < 60) return `${diffMins} dakika önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    return date.toLocaleDateString('tr-TR');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Tarih bilinmiyor';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Tarih bilinmiyor';
    return date.toLocaleDateString('tr-TR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSource = (item) => {
    if (item.source) {
      if (typeof item.source === 'string') return item.source;
      if (typeof item.source === 'object' && item.source.name) return item.source.name;
      if (typeof item.source === 'object' && item.source.url) return item.source.url;
    }
    if (item.domain) return item.domain;
    return 'Bilinmiyor';
  };

  const openNewsDetail = (item) => {
    setSelectedNews(item);
  };

  const closeNewsDetail = () => {
    setSelectedNews(null);
  };

  const openOriginalArticle = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Navbar user={user} setUser={setUser} />
      
      <div className="max-w-7xl mx-auto p-4 pb-24">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">📰 Haberler</h1>
          <p className="text-gray-400 text-sm sm:text-base">Borsa İstanbul ve BIST 100 haberleri</p>
        </div>

        {/* News List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="text-4xl mb-4">⏳</div>
              <div className="text-xl">Haberler yükleniyor...</div>
            </div>
          </div>
        ) : news.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="text-6xl mb-4">📰</div>
              <div className="text-xl text-gray-400">Henüz haber yok</div>
              <div className="text-sm text-gray-500 mt-2">API key'iniz doğru mu kontrol edin</div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {news.map((item, index) => {
              const newsId = item.id || item.url || index;
              const newsDate = item.published_at || item.date || item.created_at;
              const newsSource = getSource(item);
              const description = item.description || item.content || item.body || '';
              
              return (
                <div 
                  key={newsId} 
                  className="bg-gray-800 rounded-lg p-4 sm:p-6 hover:bg-gray-750 transition-colors cursor-pointer border border-gray-700 hover:border-blue-500"
                  onClick={() => openNewsDetail(item)}
                  data-testid={`news-card-${index}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                    {/* Haber Görseli */}
                    {item.image_url && (
                      <div className="w-full sm:w-32 h-24 sm:h-20 rounded-lg overflow-hidden flex-shrink-0">
                        <img 
                          src={item.image_url} 
                          alt={item.title}
                          className="w-full h-full object-cover"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                    
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {item.category && (
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getCategoryColor(item.category)}`}>
                            {item.category}
                          </span>
                        )}
                        {newsDate && (
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock size={12} />
                            {getTimeAgo(newsDate)}
                          </span>
                        )}
                        {newsSource && newsSource !== 'Bilinmiyor' && (
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Building size={12} />
                            {newsSource}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold mb-2 hover:text-blue-400 transition-colors">
                        {String(item.title || 'Başlık yok')}
                      </h3>
                      {description && (
                        <p className="text-sm sm:text-base text-gray-400 line-clamp-2">
                          {String(description).substring(0, 150)}...
                        </p>
                      )}
                      <div className="mt-2 text-xs text-blue-400">
                        Devamını okumak için tıklayın →
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* News Detail Modal - Mobile Responsive */}
      {selectedNews && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-3 pb-20 sm:pb-3" 
          onClick={closeNewsDetail}
          data-testid="news-detail-modal"
        >
          <div 
            className="bg-gray-800 w-full sm:max-w-2xl rounded-xl max-h-full overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex-shrink-0 bg-gray-800 border-b border-gray-700 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {selectedNews.category && (
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${getCategoryColor(selectedNews.category)}`}>
                    {selectedNews.category}
                  </span>
                )}
              </div>
              <button 
                onClick={closeNewsDetail}
                className="p-2 hover:bg-gray-700 rounded-full transition-colors"
                data-testid="close-news-modal"
              >
                <X size={22} />
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {/* Haber Görseli */}
              {selectedNews.image_url && (
                <div className="w-full h-36 sm:h-48 rounded-lg overflow-hidden mb-4">
                  <img 
                    src={selectedNews.image_url} 
                    alt={selectedNews.title}
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}

              {/* Başlık */}
              <h2 className="text-base sm:text-lg font-bold mb-3 leading-tight">
                {selectedNews.title}
              </h2>

              {/* Meta Bilgiler */}
              <div className="flex flex-wrap items-center gap-2 mb-4 text-xs text-gray-400">
                {(selectedNews.published_at || selectedNews.created_at) && (
                  <div className="flex items-center gap-1">
                    <Calendar size={12} />
                    <span>{formatDate(selectedNews.published_at || selectedNews.created_at)}</span>
                  </div>
                )}
                {getSource(selectedNews) !== 'Bilinmiyor' && (
                  <div className="flex items-center gap-1">
                    <Building size={12} />
                    <span>{getSource(selectedNews)}</span>
                  </div>
                )}
              </div>

              {/* Açıklama */}
              {selectedNews.description && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold mb-2 text-blue-400">Özet</h3>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    {selectedNews.description}
                  </p>
                </div>
              )}

              {/* İçerik */}
              {selectedNews.content && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold mb-2 text-blue-400">İçerik</h3>
                  <div className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">
                    {selectedNews.content.replace(/\[\+\d+ chars\]/, '')}
                  </div>
                </div>
              )}

              {/* Ek Bilgiler */}
              {selectedNews.body && selectedNews.body !== selectedNews.content && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold mb-2 text-blue-400">Detaylar</h3>
                  <div className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">
                    {selectedNews.body}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer - Always visible */}
            <div className="flex-shrink-0 bg-gray-800 border-t border-gray-700 px-4 py-3 flex gap-2">
              <button
                onClick={closeNewsDetail}
                className="flex-1 py-2.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-semibold transition-colors"
              >
                Kapat
              </button>
              {selectedNews.url && (
                <button
                  onClick={() => openOriginalArticle(selectedNews.url)}
                  className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                  data-testid="open-original-article"
                >
                  <ExternalLink size={16} />
                  Habere Git
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation - Dashboard Style */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-700 z-40">
        <div className="grid grid-cols-5 gap-1 px-2 py-3">
          {/* Ana Sayfa */}
          <button
            onClick={() => navigate('/dashboard')}
            className="flex flex-col items-center justify-center py-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <Home size={24} className="text-gray-400 mb-1" />
            <span className="text-xs text-gray-400">Ana Sayfa</span>
          </button>

          {/* İşlemler */}
          <button
            onClick={() => navigate('/trading')}
            className="flex flex-col items-center justify-center py-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <TrendingUp size={24} className="text-gray-400 mb-1" />
            <span className="text-xs text-gray-400">İşlemler</span>
          </button>

          {/* Pozisyonlar */}
          <button
            onClick={() => navigate('/positions')}
            className="flex flex-col items-center justify-center py-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <Briefcase size={24} className="text-gray-400 mb-1" />
            <span className="text-xs text-gray-400">Pozisyonlar</span>
          </button>

          {/* Profil */}
          <button
            onClick={() => navigate('/profile')}
            className="flex flex-col items-center justify-center py-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <User size={24} className="text-gray-400 mb-1" />
            <span className="text-xs text-gray-400">Profil</span>
          </button>

          {/* Haberler */}
          <button
            onClick={() => navigate('/news')}
            className="flex flex-col items-center justify-center py-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <Newspaper size={24} className="text-blue-400 mb-1" />
            <span className="text-xs text-blue-400">Haberler</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default NewsPage;
